import { useMemo } from 'react';
import { Grid, TimeSelector, Typography } from '@methanesat/ui-components';
import { useSelector } from 'react-redux';
import { useGetHistoricalSamplesForTarget, useTranslate } from '../../../../hooks';
import { EmissionDrawerCardContentProps, isTotalOnlyInfo, PageIds, StacDateBlock } from '../../../../types';
import { ErrorBoundary } from '../../../../utils/errorBoundary';
import { stacDatesToTimeSelector } from '../DataSpecific/EmissionsDrawers/helpers';
import { FeatureIDCaption } from '../Layout';
import { DispersedSourceAccordion } from './DispersedSourceAccordion';
import { DistinctPointSourceAccordion } from './DistinctPointSourceAccordion';
import { DrawerCardContent } from './DrawerCardContent';
import { EmissionAccordionFallback } from './EmissionAccordionFallback';
import { getUniqueAccordionId } from './utils';
import { selectPlatform } from '../../../../reducers';
import { getGlobalDateOptions, getTimeSelectorTimespan } from '../../../../consts';
import { TotalOnlyAccordion } from './TotalOnlyAccordion';
import { LinkToMain } from './LinkToMain';
import { DataUnavailableAccordion, getUnavailableAccordionId } from './DataUnavailableAccordion';

export const EmissionDrawerCardContent = ({
    targetId,
    targetGroupId,
    startDateTime,
    distinctPointEmissions,
    dispersedSourceEmissions,
    dataUnavailable,
    expandedId,
    isActive,
    handleAccordionChange,
    handleDateChange,
    ogiNearbyEnabled,
    page
}: EmissionDrawerCardContentProps) => {
    const t = useTranslate();
    const platform = useSelector(selectPlatform);
    const titleLabel = t('emissionsMapPage.mapModals.target.targetIdLabel');
    const titleValue = page === PageIds.emissionsMap ? targetGroupId || targetId : targetId;

    const selectorDates = useGetHistoricalSamplesForTarget(targetGroupId || targetId);
    const dataForTimeSelector = useMemo(() => {
        /**
         * This conditional prevents passing selectorDates = `null`
         * to `stacDatesToTimeSelector` and triggering an error.
         */
        if (selectorDates)
            return stacDatesToTimeSelector({
                dates: selectorDates,
                handleClick: (day: StacDateBlock) => {
                    handleDateChange(day, targetId);
                }
            });
        return [];
    }, [selectorDates, handleDateChange, targetId]);

    // text for caption
    const emissionIDs: string[] = [];
    distinctPointEmissions.forEach((item) => emissionIDs.push(`${item.object.itemId}-${targetId}`));
    dispersedSourceEmissions.forEach((item) => emissionIDs.push(`${item.object.id}-${targetId}`));
    const featureIDCaption = `${emissionIDs.join('\n')}`;

    const Content = (
        <>
            {/* accordions */}
            {distinctPointEmissions.map((info) => {
                const id = getUniqueAccordionId(info);
                return (
                    <ErrorBoundary
                        fallback={<EmissionAccordionFallback content={t('emissionsMapPage.error.plumeEmissionData')} />}
                        key={id}
                    >
                        <DistinctPointSourceAccordion
                            handleChange={handleAccordionChange}
                            key={id}
                            expanded={expandedId === id}
                            info={info}
                            ogiNearbyEnabled={ogiNearbyEnabled}
                        />
                    </ErrorBoundary>
                );
            })}
            {dispersedSourceEmissions.map((info) => {
                const id = getUniqueAccordionId(info);
                return (
                    <ErrorBoundary
                        fallback={<EmissionAccordionFallback content={t('emissionsMapPage.error.areaEmissionData')} />}
                        key={id}
                    >
                        {isTotalOnlyInfo(info) ? (
                            <TotalOnlyAccordion
                                handleChange={handleAccordionChange}
                                info={info}
                                expanded={expandedId === id}
                            />
                        ) : (
                            <DispersedSourceAccordion
                                handleChange={handleAccordionChange}
                                info={info}
                                expanded={expandedId === id}
                                ogiNearbyEnabled={ogiNearbyEnabled}
                            />
                        )}
                    </ErrorBoundary>
                );
            })}
            {dataUnavailable && (
                <DataUnavailableAccordion
                    expanded={expandedId === getUnavailableAccordionId(targetId)}
                    handleChange={(accordionId) => handleAccordionChange(accordionId)}
                    targetId={targetId}
                />
            )}
            {/* Observations over time */}
            {isActive && page !== PageIds.singleCollection && !ogiNearbyEnabled && (
                <Grid size="grow">
                    <Grid container marginTop={3} marginBottom={3} rowGap={2}>
                        <Grid size={{ xs: 12 }}>
                            <Typography fontWeight="bold">
                                {t('emissionsMapPage.mapModals.common.overTimeTitle')}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TimeSelector
                                boxHeight="35px"
                                data-testid={`drawer-with-cards-date-picker-target-${targetId}`}
                                data={dataForTimeSelector}
                                globalDateOptions={getGlobalDateOptions(platform)}
                                selectedValue={startDateTime}
                                variant={getTimeSelectorTimespan(platform)}
                                layout="horizontal"
                            />
                        </Grid>
                    </Grid>
                    <FeatureIDCaption caption={featureIDCaption} />
                </Grid>
            )}
            {page === PageIds.singleCollection && (
                <Grid size="grow">
                    <LinkToMain targetId={targetId} startDateTime={startDateTime} />
                </Grid>
            )}
        </>
    );
    return <DrawerCardContent {...{ titleLabel, titleValue, Content }} />;
};
